/**
 * Typen und Hilfen der Seite „Blitzschutz, Erdung und Überspannung"
 * (W2-C1/W2-C2, 16.10.2026). Die Felder entsprechen der JSON-Route
 * `GET …/elektro/blitzschutz` (Blitzschutzlauf).
 */

export const PDF_HINWEIS =
  'Nachweis und Überspannung: Der PDF-Abruf legt nur bei geändertem Stand eine neue Berichtsfassung mit neuer '
  + 'Dokumentnummer an; sonst liefert er die abgelegte Fassung (F11).';

/** Der Plan der Fangeinrichtung zeichnet mit Einfang (TX-STANDARD S3.6) - er bleibt bei „neu je Abruf“. */
export const PLAN_PDF_HINWEIS =
  'Der Plan legt bei jedem Abruf eine NEUE Berichtsfassung mit neuer Dokumentnummer an (R25) — '
  + 'nur abrufen, wenn eine Fassung gebraucht wird.';

export const ANNAHMEN_HINWEIS =
  'Blitzschutzklasse und Eckwerte sind aus der Risikoanalyse übernommen (Fachfrage ELT-60), die Dachhöhe '
  + 'ist aus den Fußpunkten der Fangstangen abgeleitet, Normwerte sind ungeprüft. Überschreitungen sind '
  + 'deshalb Warnungen, keine Fehler.';

export interface BsKennzahlen {
  klasse: number | null; kugelradiusM: number | null; fangstangen: number; dachaufbauten: number;
  geschuetzt: number; ungeschuetzt: number; kleinsteReserveM: number | null; trennungVerletzt: number;
  ableitungenErforderlich: number; verteiler: number; verteilerOhneSpd: number; befunde: number;
  warnungen: number; fehler: number; aufAnnahmen: boolean;
}

export interface BsVergleich {
  groesse: string; einheit: string; gerechnet: number; dokument: number | null;
  abweichungProzent: number | null; status: string;
}

export interface BsKlasse {
  nummer: number; name: string; kugelradiusM: number; maschenweiteM: number; ableitungsabstandM: number;
  ki: number; scheitelstromKa: number;
}

export interface BsStange {
  kennung: string; typ: string | null; x: number; y: number; zFuss: number; zSpitze: number; hoeheM: number;
  schutzradiusDachM: number; trennungsabstandM: number | null; naechstesObjekt: string | null;
  abstandObjektM: number | null; trennungOk: boolean;
}

export interface BsAufbau {
  kennung: string; art: string; x: number; y: number; maxZ: number; schutzhoehe: number | null;
  reserveM: number | null; geschuetzt: boolean; naechsteStange: string | null; abstandStangeM: number | null;
}

export interface BsAbleitung {
  umfangM: number; abstandSollM: number; erforderlich: number; durchgehendModelliert: number; nurDach: number;
  wandanschlusspunkte: number; wandanschlusspunkteBoden: number; anschlussfahnen: number;
  erdungsfestpunkte: number; natuerlicheAbleitungen: string; status: string; befunde: string[];
}

export interface BsErdung {
  fundamenterderM: number; ringerderM: number; sonstigeM: number; strecken: number; umfangM: number;
  flaecheM2: number; mittlererRadiusM: number; l1M: number | null; typBErfuellt: boolean | null;
  groessteMaschenweiteM: number | null; maschenweiteZulM: number | null; stelleX: number | null;
  stelleY: number | null; maschenweiteErfuellt: boolean | null; befunde: string[];
}

export interface BsSpdZeile {
  kennzeichen: string; art: string; versorgungsart: string; ober: string | null; ist: string | null;
  soll: string | null; zuleitungLaengeM: number | null; status: string; befunde: string[];
}

export interface BsUeberspannung {
  pflicht: boolean; pflichtGruende: string[];
  anforderungen: {
    scheitelstromKa: number; anteil: number; leitungen: number; leiter: number; iimpLnKa: number;
    iimpNpeKa: number; anschlussLaengeM: number;
    schutzpegel: { kategorie: string; uwKv: number; upMaxKv: number }[];
  };
  zeilen: BsSpdZeile[]; ok: number; warnungen: number; ohneSoll: number; befunde: string[];
}

export interface BsPotentialausgleich {
  groessterLeiterMm2: number | null; groessterPeMm2: number | null; hauptPaMm2: number; hauptPaHerleitung: string;
  schienen: number; schienenOhneVerteiler: number; schienenOhneRaum: number;
  liste: { kennung: string; raumnummer: string | null; geschoss: string | null; verteiler: string | null }[];
  gruppen: { gruppe: string; trafos: number; nshv: string[]; zepAngegeben: boolean; status: string }[];
  befunde: string[];
}

export interface BsVorgabe {
  feld: string; wert: string; einheit: string; bestaetigt: boolean; herkunft: string; normbezug: string;
  migration: string; rueckfall: boolean;
}

export interface BlitzschutzStand {
  kennzahlen: BsKennzahlen;
  risiko: { ad: number; am: number; nd: number; nm: number; vergleiche: BsVergleich[]; risikoUnterRt: boolean; befunde: string[] };
  klasse: BsKlasse | null;
  stangen: BsStange[];
  dachaufbauten: BsAufbau[];
  dachaufbautenJeArt: { art: string; anzahl: number; ungeschuetzt: number }[];
  ableitung: BsAbleitung;
  erdung: BsErdung;
  erderhoehen: {
    art: string; gezaehlt: number; gezaehltModellM: number; gezaehltGrundrissM: number; quer: number; querM: number;
    darueber: number; darueberM: number; darueberZMinM: number | null; darueberZMaxM: number | null;
  }[];
  ueberspannung: BsUeberspannung;
  potentialausgleich: BsPotentialausgleich;
  vorgaben: BsVorgabe[];
  befunde: string[];
  stand: string;
}

export type Stufe = 'fehler' | 'warnung' | 'hinweis';

/** "WARNUNG  Text" → { stufe: 'warnung', text: 'Text' }. */
export function zerlegeBefund(befund: string): { stufe: Stufe; text: string } {
  const i = befund.indexOf('  ');
  const kopf = i > 0 ? befund.slice(0, i) : '';
  const text = i > 0 ? befund.slice(i + 2) : befund;
  const stufe: Stufe = kopf === 'FEHLER' ? 'fehler' : kopf === 'WARNUNG' ? 'warnung' : 'hinweis';
  return { stufe, text };
}
